import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  Input,
  OnDestroy,
} from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormControl,
  ValidatorFn,
} from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  tap,
  finalize,
  startWith,
  catchError,
} from 'rxjs/operators';
import {
  IFilterContentParams,
  FilterContentParams,
} from '../../core/models/i-filter-content';
import { ContentList, IContentList } from '../../core/models/i-content-list';
import { Subscription, Observable, of } from 'rxjs';
import {
  IContentListResponse,
  ContentListResponse,
} from '../../core/models/i-content-list-response';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { ContentListService } from '../shared/services/cmsapi/content-list.service';
import { AuthorService } from 'src/app/shared/services/author.service';
import { IAuthor } from '../../core/models/i-author';
import {
  AuthorResponse,
  IAuthorResponse,
} from 'src/app/core/models/i-author-response';

@Component({
  selector: 'app-filter-content-list',
  templateUrl: './filter-content-list.component.html',
})
export class FilterContentListComponent implements OnInit, OnDestroy {
  selectedContentListName = new UntypedFormControl();
  selectedAuthorName = new UntypedFormControl();

  isLoading = false;
  pageSubscriptions: Subscription[] = [];
  @Output() filter = new EventEmitter<IFilterContentParams>();
  @Input() deliveryTargetCodes: string[] = [];
  contentFilters = new UntypedFormGroup(
    {
      headline: new UntypedFormControl(''),
      publishDateFrom: new UntypedFormControl(),
      publishDateTo: new UntypedFormControl(),
      authors: new UntypedFormControl(),
      contentListId: new UntypedFormControl(),
    },
    this.publishedDateRangeValidator('publishDateFrom', 'publishDateTo'),
  );
  debounceTme: number = 1000;
  options: IContentList[] = [];
  authors: IAuthor[] = [];

  constructor(
    private contentListService: ContentListService,
    private authorService: AuthorService,
    private dialogService: DialogService,
  ) {}

  ngOnInit(): void {
    const selectedContentListNameSubscription =
      this.selectedContentListName.valueChanges
        .pipe(
          tap(() => (this.isLoading = true)),
          debounceTime(this.debounceTme),
          distinctUntilChanged(),
          switchMap((value) =>
            typeof value === 'string'
              ? this.searchFilteredContentLists(value)
              : this.searchFilteredContentLists(value.name),
          ),
        )
        .subscribe(
          (contentLists) => {
            this.options = contentLists.results;
            this.isLoading = false;
          },
          (error) => {
            this.isLoading = false;
            this.dialogService.openErrorDialog({
              message:
                'An error has occurred whilst loading the content. Please contact IT.',
            });
          },
        );

    const authorSearchSubscription = this.selectedAuthorName.valueChanges
      .pipe(
        tap(() => (this.isLoading = true)),
        debounceTime(this.debounceTme),
        distinctUntilChanged(),
        switchMap((value) =>
          typeof value === 'string'
            ? this.searchAuthors(value)
            : this.searchAuthors(value.name),
        ),
      )
      .subscribe(
        (authorResponse) => {
          this.authors = authorResponse.results;
          this.isLoading = false;
        },
        (error) => {
          this.isLoading = false;
          this.dialogService.openErrorDialog({
            message:
              'An error has occurred whilst loading the authors. Please contact IT.',
          });
        },
      );

    const contentFiltersSubscription = this.contentFilters.valueChanges
      .pipe(debounceTime(this.debounceTme), distinctUntilChanged())
      .subscribe((newFilters: FilterContentParams) => {
        if (this.contentFilters.invalid) {
          return;
        }
        this.filter.emit(newFilters);
      });

    this.pageSubscriptions.push(
      selectedContentListNameSubscription,
      authorSearchSubscription,
      contentFiltersSubscription,
    );
  }

  ngOnDestroy(): void {
    this.pageSubscriptions.forEach((subscription) =>
      subscription.unsubscribe(),
    );
  }

  searchFilteredContentLists(
    searchText: string,
  ): Observable<IContentListResponse> {
    searchText = searchText.replace(/[^a-zA-Z0-9 ]/g, '');
    if (!searchText) {
      this.clearContentListId();
      return of(new ContentListResponse());
    }
    return this.contentListService.getFiltered$(
      new ContentList(this.deliveryTargetCodes, searchText),
    );
  }

  searchAuthors(searchText: string): Observable<IAuthorResponse> {
    if (!searchText) {
      this.clearAuthorId();
      return of(new AuthorResponse());
    }

    return this.authorService.getFilteredBySearchText$(searchText);
  }

  clearContentListNameValue(): void {
    this.selectedContentListName.setValue('');
    this.clearContentListId();
  }

  clearAuthorNameValue(): void {
    this.selectedAuthorName.setValue('');
    this.clearAuthorId();
  }

  private clearAuthorId(): void {
    if (this.contentFilters.get('authors').value) {
      this.contentFilters.patchValue({ authors: '' });
    }
  }

  private clearContentListId(): void {
    if (this.contentFilters.get('contentListId').value) {
      this.contentFilters.patchValue({ contentListId: '' });
    }
  }
  displayContentListNames(contentList: IContentList): string {
    return contentList && contentList.name ? contentList.name : '';
  }

  displayAuthorNames(author: IAuthor): string {
    return author && author.name ? author.name : '';
  }

  getSelectedContentListOption(id: string): void {
    this.contentFilters.patchValue({ contentListId: id });
  }

  getSelectedAuthorOption(id: string): void {
    this.contentFilters.patchValue({ authors: id });
  }

  publishedDateRangeValidator(from: string, to: string): ValidatorFn {
    return (group: UntypedFormGroup): { [key: string]: any } => {
      const fromControl = group.controls[from];
      const toControl = group.controls[to];
      if (
        fromControl.value &&
        toControl.value &&
        fromControl.value > toControl.value
      ) {
        return {
          publishDateRangeError: true,
        };
      }
      return {};
    };
  }
}
